import { useState } from "react";
import { ChevronDown, ChevronUp, Linkedin, Globe, Briefcase, GraduationCap, Wallet, Users, Quote } from "lucide-react";
import { FUND_MODEL_LABEL, SEARCH_STAGE_LABEL } from "../lib/taxonomy";
import { fmtEur } from "./ImFinancialViz";
import { t } from "../lib/i18n";

const INVESTMENT_LABEL = { equity: "Equity", debt: "Deuda", mixed: "Mixto" };
const DEDICATION_LABEL = { full_time: t("Dedicación completa"), part_time: t("Dedicación parcial") };

const Item = ({ icon: Icon, label, value }) =>
  value ? (
    <div className="flex items-start gap-2 text-sm">
      <Icon className="w-3.5 h-3.5 mt-0.5 text-gold flex-shrink-0" />
      <span><span className="text-muted-foreground">{label}: </span>{value}</span>
    </div>
  ) : null;

const ExtLink = ({ href, icon: Icon, label, testid }) =>
  href ? (
    <a href={href.startsWith("http") ? href : `https://${href}`} target="_blank" rel="noreferrer"
       className="inline-flex items-center gap-1.5 font-mono-label text-[#1E3A5F] hover:text-[#C25F1E] underline underline-offset-4"
       data-testid={testid}>
      <Icon className="w-3.5 h-3.5" /> {label}
    </a>
  ) : null;

const ticketText = (p) =>
  p.ticket_min_eur || p.ticket_max_eur
    ? `${p.ticket_min_eur ? fmtEur(p.ticket_min_eur) : "—"} – ${p.ticket_max_eur ? fmtEur(p.ticket_max_eur) : "sin límite"}`
    : null;

export default function SignerProfileCard({ sig }) {
  const [open, setOpen] = useState(false);
  const p = sig.signer_profile || {};
  const isSearcher = sig.role === "searcher";
  const hasLinks = p.linkedin_url || p.website_url;
  const empty = Object.keys(p).filter((k) => !["picture", "member_since"].includes(k)).length === 0;

  return (
    <div className="w-full mt-3" data-testid={`signer-profile-${sig.signature_id}`}>
      <div className="flex flex-wrap items-center gap-4">
        <ExtLink href={p.linkedin_url} icon={Linkedin} label={t("LinkedIn")} testid={`signer-linkedin-${sig.signature_id}`} />
        <ExtLink href={p.website_url} icon={Globe} label="Web" testid={`signer-website-${sig.signature_id}`} />
        <button type="button" onClick={() => setOpen((v) => !v)}
                className="inline-flex items-center gap-1 font-mono-label text-muted-foreground hover:text-foreground"
                data-testid={`signer-profile-toggle-${sig.signature_id}`}>
          {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          {open ? t("Ocultar perfil") : isSearcher ? t("Ver perfil del searcher") : t("Ver perfil del inversor")}
        </button>
        {!hasLinks && !open && <span className="text-xs text-muted-foreground italic">{t("Sin enlaces públicos")}</span>}
      </div>

      {open && (
        <div className="mt-3 hairline bg-bone p-5 grid md:grid-cols-2 gap-3" data-testid={`signer-profile-body-${sig.signature_id}`}>
          {empty && <p className="text-sm text-muted-foreground italic md:col-span-2">{t("Este usuario aún no ha completado su perfil.")}</p>}
          {isSearcher ? (
            <>
              <Item icon={Briefcase} label={t("Posición actual")} value={p.current_position} />
              <Item icon={Briefcase} label={t("Experiencia")} value={p.years_experience != null ? `${p.years_experience} años` : null} />
              <Item icon={GraduationCap} label={t("Formación")} value={p.education} />
              <Item icon={Users} label="Background" value={p.background?.join(", ")} />
              <Item icon={Wallet} label={t("Search fund")} value={[p.fund_name, FUND_MODEL_LABEL[p.fund_model] || p.fund_model, SEARCH_STAGE_LABEL[p.search_stage] || p.search_stage].filter(Boolean).join(" · ")} />
              <Item icon={Wallet} label={t("Capital comprometido")} value={p.committed_capital_eur ? `${fmtEur(p.committed_capital_eur)}${p.backers_count ? ` · ${p.backers_count} inversores` : ""}` : null} />
              <Item icon={Users} label={t("Dedicación")} value={DEDICATION_LABEL[p.dedication] || p.dedication} />
              <Item icon={Briefcase} label={t("Sectores")} value={p.sectors_interest?.join(", ")} />
              {p.motivation && (
                <div className="md:col-span-2 flex items-start gap-2 text-sm">
                  <Quote className="w-3.5 h-3.5 mt-0.5 text-gold flex-shrink-0" />
                  <p className="italic text-muted-foreground">"{p.motivation}"</p>
                </div>
              )}
            </>
          ) : (
            <>
              <Item icon={Briefcase} label={t("Organización")} value={p.organization} />
              <Item icon={Wallet} label={t("Tipo de inversión")} value={INVESTMENT_LABEL[p.investment_type] || p.investment_type} />
              <Item icon={Wallet} label={t("Ticket")} value={ticketText(p)} />
              <Item icon={Users} label={t("Modelos que respalda")} value={p.fund_models_backed?.map((m) => FUND_MODEL_LABEL[m] || m).join(", ")} />
              <Item icon={Briefcase} label={t("Sectores")} value={p.sectors_interest?.join(", ")} />
              <Item icon={Globe} label={t("Geografías")} value={p.geographies?.slice(0, 6).join(", ")} />
              {p.bio && (
                <div className="md:col-span-2 flex items-start gap-2 text-sm">
                  <Quote className="w-3.5 h-3.5 mt-0.5 text-gold flex-shrink-0" />
                  <p className="italic text-muted-foreground">"{p.bio}"</p>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
